/* eslint-disable max-classes-per-file */
import lcrRequest from 'lcrRequest';
import { transformAPIError } from 'utils';
import { APIError, ErrorLog } from 'utils/LoggerItem';
import {
  BETSHOP_CONFIG_MAX_AGE,
  HTTP_STATUS_CODE_PAYMENT_REQUIRED,
} from 'constants/bcms';
import { RESPONSE_HTTP_OK } from 'constants/app';
import { pickBy as _pickBy } from 'lodash';
import {
  getOperatorUrlParams,
  getTrackingParams,
} from 'newDesign/modules/common/utils/buildUrlParams';
import fallbackDefaultTranslations from 'localization/fallbackDefaultTranslations';
import { buildQueryString } from '../../src/utils/Url';

class BCMSFeed {
  constructor(feed, params = undefined, isOperator = 0) {
    const lcrParams = isOperator
      ? buildQueryString(params)
      : '';

    if (feed === 'BetshopConfig') {
      if (isOperator) {
        this.url = '/feeds/betshop-config' + lcrParams;
        // For non-operators
        // add "cid" and "bid" to track which client/betshop uses old / new domain
      } else {
        this.url = '/feeds/betshop-config';

        const trackingParams = getTrackingParams({
          clientId: params.cid,
          betshopId: params.bid,
          betshopUserId: params.id,
        });

        if (trackingParams) {
          this.url = `/feeds/betshop-config?${trackingParams}`;
        }
      }
    } else if (feed === 'NextMatches') {
      this.url = '/feeds/next-matches' + lcrParams;
    } else if (feed === 'HighlightsOfTheDay') {
      this.url = '/feeds/highlights-of-the-day' + lcrParams;
    } else if (feed === 'ChannelStageConfig') {
      const queryString = getOperatorUrlParams(params.id, 'stage', isOperator);

      this.url = queryString
        ? `/feeds/channel-stage-config?${queryString}`
        : '/feeds/channel-stage-config';
    } else if (feed === 'Translations') {
      const queryString = getOperatorUrlParams(params.id, 'lang', isOperator);

      this.url = queryString
        ? `/api/translations?${queryString}`
        : '/api/translations';
    }

    this.maxAge = 5;
  }

  getMaxAge() {
    return this.maxAge;
  }

  get() {
    return lcrRequest
      .get(this.url)
      .then(data => {
        if (data.code === HTTP_STATUS_CODE_PAYMENT_REQUIRED) {
          return data;
        }

        if (data.error) {
          throw data;
        }

        return data.doc[0];
      })
      .catch(transformAPIError);
  }

  getTranslations() {
    return lcrRequest
      .get(this.url)
      .then(data => {
        if (data.status !== RESPONSE_HTTP_OK) {
          throw data.doc[0].data.message;
        }

        return data;
      });
  }
}

export class BetshopConfigRequest extends BCMSFeed {
  constructor(userId, clientId, betshopId, isOperator) {
    const rawParams = {
      id: userId,
      cid: isOperator ? undefined : clientId,
      bid: isOperator ? undefined : betshopId,
    };

    // Remove null and undefined values, to ensure that "cid" is passed
    // It is possible that "cid" is empty when we have 1st request to the betshop-config
    // or when we simulat channels and switch between them
    const params = _pickBy(rawParams, value => value != null);

    super('BetshopConfig', params, isOperator);
  }

  normalizeData(response) {
    if (response.code === HTTP_STATUS_CODE_PAYMENT_REQUIRED) {
      return response;
    }

    const {
      data,
      _maxage,
    } = response;
    this.maxAge = _maxage || BETSHOP_CONFIG_MAX_AGE;

    if (!data || response.data.code === 0) {
      const { message } = response.data;
      throw new APIError(
        'BCMS API error',
        message,
      );
    }
    return {
      betshopConfig: data,
    };
  }

  get() {
    return super
      .get()
      .then(response => {
        if (response.error) {
          throw response;
        }
        return this.normalizeData(response);
      })
      .catch(error => transformAPIError(error));
  }
}

class OddsPackageMarketsRequest extends BCMSFeed {
  constructor(packageId) {
    super('OddsPackageMarkets', { id: packageId });
  }
}

export class BetshopOddsPanelSettingsRequest extends BCMSFeed {
  constructor(betshopId, sportId) {
    super('BetshopOddsPanelSettings', {
      id: betshopId,
      sport_id: sportId,
    });
  }

  exec() {
    return super
      .get()
      .then(response => {
        if (response.data.package === null) {
          return null;
        }
        const request = new OddsPackageMarketsRequest(response.data.package);
        return request.get();
      })
      .catch(() => null);
  }
}


export class NextMatchesRequest extends BCMSFeed {
  constructor(userId, isOperator) {
    super('NextMatches', {
      id: userId,
    }, isOperator);
  }

  static normalizeData(response) {
    const normalizedMatches = response.data.map(item => {
      const {
        status,
        ts,
        alt,
        matchid,
      } = item;

      return {
        displaySolution: item.display_solution,
        status,
        timestamp: ts,
        alternativeEvent: alt,
        matchId: parseInt(matchid, 10),
      };
    });
    return normalizedMatches;
  }

  get() {
    return super
      .get()
      .then(response => NextMatchesRequest.normalizeData(response))
      .catch(error => error);
  }
}

export class HighlightsOfTheDayRequest extends BCMSFeed {
  constructor(userId, isOperator) {
    super('HighlightsOfTheDay', {
      id: userId,
    }, isOperator);
  }
}

export class TranslationsRequest extends BCMSFeed {
  constructor(userId, isOperator) {
    super('Translations', {
      id: userId,
    }, isOperator);
  }

  get() {
    return super
      .getTranslations()
      .then(response => response)
      .catch(error => {
        const msg = new ErrorLog(`Translations Request error: ${error}`);
        return fallbackDefaultTranslations;
      });
  }
}

export class ChannelStageConfigRequest extends BCMSFeed {
  constructor(userId, stageId, isOperator) {
    super('ChannelStageConfig', {
      id: userId,
      stage: stageId,
    }, isOperator);
  }
}
